/**
 * Phase G — thin compatibility wrapper around the real i18next instance
 * (i18n/config.ts). Preserves the EXACT `t(key, vars?)` call signature
 * Phase F's own resolver used, so the ~800 existing call sites across the
 * frontend needed zero rewriting for the real-library migration.
 *
 * Key format unchanged: "namespace:key.path" — e.g.
 * t('lesson:blockOutcome.advance.heading'). i18next is configured
 * (i18n/config.ts) with `nsSeparator: ':'` and `keySeparator: false` so
 * the namespace selects which catalog/en/<namespace>.json file to read
 * from, and "key.path" is looked up as a literal (flat) key inside it —
 * the dots are part of the key string itself, never nested JSON objects,
 * exactly as before.
 *
 * Interpolation syntax unchanged too: single braces, `{varName}` (see
 * i18n/config.ts's `interpolation.prefix`/`suffix`).
 *
 * Deliberately no import.meta.env / DEV-only gate here: this module is
 * imported by plain .ts files that must also compile under the root
 * (CommonJS, ts-jest) jest config — see blockOutcomeCopy.ts/retakeOffer.ts's
 * own "no '@/' alias" comments for the same constraint. `import.meta` is a
 * syntax error under CommonJS, not just a runtime no-op, so it can't be
 * guarded with a runtime check — i18n/config.ts avoids it for the same
 * reason (see its own comment on why it uses plain `import()` instead of
 * `import.meta.glob` for lazy-loading non-English catalogues).
 */
import i18next, { initI18n } from './config';

// Synchronous by construction (see config.ts's initImmediate:false +
// synchronous English resolution) — safe to call unconditionally at
// module load, exactly like Phase F's resolver reading its static
// catalog import at module load.
initI18n();

export type Vars = Record<string, string | number | undefined>;

export function t(key: string, vars?: Vars): string {
  const sep = key.indexOf(':');
  if (sep === -1) {
    console.warn(`[i18n] malformed key (expected "namespace:key"): ${key}`);
    return key;
  }
  const result = i18next.t(key, vars as Record<string, unknown>);
  return typeof result === 'string' ? result : key;
}
